import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { Row, Table } from "@tanstack/react-table";
import moment from "moment-timezone";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IndeterminateCheckbox } from "@/section/cfr/components/IndeterminateCheckbox";
import { useToast } from "@/components/ui/use-toast";
import {
  contractChangeLoadLimit,
  useBulkPairContractChanges,
  useContractMatchProposals,
  useListContractChanges,
  usePairContractChange,
  type AgtechContractMatchCandidate,
  type BulkPairContractChangesResult,
  type ContractChangeDto,
  type ContractMatchProposalResult,
  type ContractMatchTargetType,
} from "@/lib/api/contractMatching";
import {
  BulkPairContractChangesRequestValidation,
  PairContractChangeItemValidation,
  type PairContractChangeItem,
} from "@/lib/validation/contractMatching";
import { formatNumberToDisplay } from "@/lib/api/common";
import { CommodityOptions } from "@/lib/api/commodity";
import { CommonDestinationTimezone } from "@/lib/utils";
import {
  formatContractCalendarDate,
  parseContractCalendarDate,
  rankCandidateRows,
} from "./ContractsMatchingDashboard.utils";

const PAGE_SIZE = 50;
const noProposals: ContractMatchProposalResult[] = [];

type SelectedCandidate = {
  changeId: string;
  contractNumber: number;
};

type CandidateSelectionRow = SelectedCandidate & {
  isProposedMatch: boolean;
  customer: string;
  clientId: string;
  contractType: string;
  commodity: string;
  quantity: string | null;
  contractDate: string | null;
  location: string | null;
};

// Selection-derived values are stored on the row instead of being read from
// component state inside accessors: TanStack Table caches accessor results per
// row and only rebuilds rows when the `data` reference changes.
type MatchingTableRow = {
  proposal: ContractMatchProposalResult;
  selected: SelectedCandidate | undefined;
  matchStatus: string;
  reason: string;
};

type MatchingState = {
  page: number;
  selectionVersion: number;
  selectedCandidates: Record<string, SelectedCandidate | null>;
  activeTargetId: string | null;
  pendingItems: PairContractChangeItem[];
  isSubmitting: boolean;
  bulkResult: BulkPairContractChangesResult | null;
};

type MatchingAction =
  | { type: "set_page"; page: number }
  | { type: "open_candidates"; targetId: string }
  | { type: "close_candidates" }
  | { type: "select_candidate"; targetId: string; candidate: SelectedCandidate }
  | { type: "clear_candidate"; targetId: string }
  | { type: "request_pairing"; items: PairContractChangeItem[] }
  | { type: "cancel_pairing" }
  | { type: "start_submit" }
  | { type: "submit_failed" }
  | { type: "single_completed" }
  | { type: "bulk_completed"; result: BulkPairContractChangesResult }
  | { type: "close_bulk_result" };

const initialState: MatchingState = {
  page: 1,
  selectionVersion: 0,
  selectedCandidates: {},
  activeTargetId: null,
  pendingItems: [],
  isSubmitting: false,
  bulkResult: null,
};

function matchingReducer(state: MatchingState, action: MatchingAction): MatchingState {
  switch (action.type) {
    case "set_page":
      return {
        ...state,
        page: action.page,
        selectionVersion: state.selectionVersion + 1,
        activeTargetId: null,
        pendingItems: [],
      };
    case "open_candidates":
      return { ...state, activeTargetId: action.targetId };
    case "close_candidates":
      return { ...state, activeTargetId: null };
    case "select_candidate":
      return {
        ...state,
        selectedCandidates: {
          ...state.selectedCandidates,
          [action.targetId]: action.candidate,
        },
        activeTargetId: null,
      };
    case "clear_candidate":
      return {
        ...state,
        selectedCandidates: {
          ...state.selectedCandidates,
          [action.targetId]: null,
        },
        activeTargetId: null,
      };
    case "request_pairing":
      return { ...state, pendingItems: action.items };
    case "cancel_pairing":
      return { ...state, pendingItems: [] };
    case "start_submit":
      return { ...state, isSubmitting: true };
    case "submit_failed":
      return { ...state, pendingItems: [], isSubmitting: false };
    case "single_completed":
      return {
        ...state,
        pendingItems: [],
        isSubmitting: false,
        selectionVersion: state.selectionVersion + 1,
      };
    case "bulk_completed":
      return {
        ...state,
        pendingItems: [],
        isSubmitting: false,
        selectionVersion: state.selectionVersion + 1,
        bulkResult: action.result,
      };
    case "close_bulk_result":
      return { ...state, bulkResult: null };
  }
}

function hasSelectionOverride(
  proposal: ContractMatchProposalResult,
  selectedCandidates: Record<string, SelectedCandidate | null>,
): boolean {
  return Object.prototype.hasOwnProperty.call(
    selectedCandidates,
    proposal.target.target_id,
  );
}

function selectedCandidate(
  proposal: ContractMatchProposalResult,
  selectedCandidates: Record<string, SelectedCandidate | null>,
): SelectedCandidate | undefined {
  if (hasSelectionOverride(proposal, selectedCandidates)) {
    return selectedCandidates[proposal.target.target_id] ?? undefined;
  }
  if (proposal.outcome !== "unique_match") return undefined;
  return {
    changeId: proposal.candidates[0].change_id,
    contractNumber: proposal.candidates[0].source_contract_id,
  };
}

function isManuallyMatched(
  proposal: ContractMatchProposalResult,
  selectedCandidates: Record<string, SelectedCandidate | null>,
): boolean {
  return hasSelectionOverride(proposal, selectedCandidates)
    && selectedCandidates[proposal.target.target_id] !== null;
}

function isSelectionCleared(
  proposal: ContractMatchProposalResult,
  selectedCandidates: Record<string, SelectedCandidate | null>,
): boolean {
  return hasSelectionOverride(proposal, selectedCandidates)
    && selectedCandidates[proposal.target.target_id] === null;
}

function proposalStatus(proposal: ContractMatchProposalResult): string {
  switch (proposal.outcome) {
    case "unique_match":
      return "Exact match";
    case "ambiguous":
      return "Multiple matches";
    case "no_match":
      return "No match";
  }
}

const proposalReasonLabels: Record<ContractMatchProposalResult["reason"], string> = {
  target_missing_primary_evidence: "Contract is missing required matching information",
  no_primary_candidates: "No Agtech contracts match the required fields",
  secondary_criteria_eliminated_candidates: "Additional contract details did not match",
  unique_primary_candidate: "One Agtech contract matches all required fields",
  unique_after_secondary_criteria: "One Agtech contract matches after comparing additional details",
  multiple_candidates_after_secondary_criteria: "Multiple Agtech contracts match the available details",
};

function reasonLabel(reason: ContractMatchProposalResult["reason"]): string {
  return proposalReasonLabels[reason] ?? reason.replaceAll("_", " ");
}

function commodityDisplayName(value: string): string {
  return CommodityOptions.find(
    (option: (typeof CommodityOptions)[number]): boolean => option.value === value,
  )?.label ?? value;
}

function normalizeAgtechPurchaseSaleType(value: string): string {
  if (value === "P/C" || value === "P") return "P";
  if (value === "S/C" || value === "S") return "S";
  return value;
}

function candidateMatchesTargetType(
  candidate: CandidateSelectionRow,
  targetType: ContractMatchTargetType,
): boolean {
  const isOtc = /^otc/i.test(candidate.contractType);
  return targetType === "trading_contract" ? isOtc : !isOtc;
}

function CandidateSelectionDialog({
  proposal,
  proposals,
  selectedChangeId,
  selectedCandidates,
  onClear,
  onClose,
  onSelect,
}: {
  proposal: ContractMatchProposalResult | null;
  proposals: ContractMatchProposalResult[];
  selectedChangeId: string | undefined;
  selectedCandidates: Record<string, SelectedCandidate | null>;
  onClear: () => void;
  onClose: () => void;
  onSelect: (candidate: CandidateSelectionRow) => void;
}) {
  const { toast } = useToast();
  const limitNoticeTargetId = useRef<string | null>(null);
  const listResult = useListContractChanges(proposal !== null);
  const pages = listResult.data?.pages ?? [];
  const listedCandidates = pages.flatMap((page): ContractChangeDto[] =>
    page.success ? page.data.items : [],
  ).filter(
    (candidate: ContractChangeDto): boolean =>
      proposal?.target.target_type !== "trading_contract"
      || normalizeAgtechPurchaseSaleType(candidate.contract_type) === "P",
  );
  const failedPage = pages.find((page): boolean => !page.success);
  const proposedCandidates = proposal?.candidates ?? [];
  const proposedChangeIds = new Set(
    proposedCandidates.map(
      (candidate: AgtechContractMatchCandidate): string => candidate.change_id,
    ),
  );
  const candidateRowsById = new Map<string, CandidateSelectionRow>();
  listedCandidates.forEach((candidate: ContractChangeDto): void => {
    candidateRowsById.set(candidate.id, {
      changeId: candidate.id,
      contractNumber: candidate.contract_id,
      isProposedMatch: proposedChangeIds.has(candidate.id),
      customer: candidate.vendor_customer_id === proposal?.target.Customer?.client_id
        ? proposal.target.Customer.long_name
        : "",
      clientId: candidate.vendor_customer_id,
      contractType: candidate.pricing_type_code,
      commodity: candidate.commodity_id,
      quantity: candidate.quantity,
      contractDate: candidate.contract_date,
      location: candidate.plc_id,
    });
  });
  proposedCandidates.forEach((candidate: AgtechContractMatchCandidate): void => {
    if (candidateRowsById.has(candidate.change_id)) return;
    candidateRowsById.set(candidate.change_id, {
      changeId: candidate.change_id,
      contractNumber: candidate.source_contract_id,
      isProposedMatch: true,
      customer: candidate.primary.customer_client_id === proposal?.target.Customer?.client_id
        ? proposal.target.Customer.long_name
        : "",
      clientId: candidate.primary.customer_client_id ?? "",
      contractType: candidate.primary.contract_type,
      commodity: candidate.primary.commodity,
      quantity: candidate.primary.quantity,
      contractDate: candidate.primary.contract_date,
      location: candidate.fallback.location_external_id,
    });
  });
  const selectedElsewhere = new Set(
    proposals.flatMap(
      (otherProposal: ContractMatchProposalResult): string[] => {
        if (otherProposal.target.target_id === proposal?.target.target_id) return [];
        const otherSelectedCandidate = selectedCandidate(
          otherProposal,
          selectedCandidates,
        );
        return otherSelectedCandidate ? [otherSelectedCandidate.changeId] : [];
      },
    ),
  );
  const availableCandidates = [...candidateRowsById.values()].filter(
    (candidate: CandidateSelectionRow): boolean =>
      proposal !== null
      && candidateMatchesTargetType(candidate, proposal.target.target_type)
      && !selectedElsewhere.has(candidate.changeId),
  );
  const candidates = proposal
    ? rankCandidateRows(availableCandidates, proposal)
    : availableCandidates;

  useEffect((): void => {
    const targetId = proposal?.target.target_id ?? null;
    if (!targetId) {
      limitNoticeTargetId.current = null;
      return;
    }
    if (listResult.reachedLimitWithMoreAvailable && limitNoticeTargetId.current !== targetId) {
      limitNoticeTargetId.current = targetId;
      toast({
        title: `Showing the first ${contractChangeLoadLimit} Agtech contracts`,
        description: `Only the first ${contractChangeLoadLimit} unmatched Agtech contracts were loaded. The current suggested matches are also shown.`,
      });
    }
  }, [proposal, listResult.reachedLimitWithMoreAvailable, toast]);

  const columns: DataTableColumn<CandidateSelectionRow>[] = [
    {
      id: "selected",
      header: "Selected",
      accessorFn: (candidate: CandidateSelectionRow): string =>
        candidate.changeId === selectedChangeId ? "Yes" : "",
      size: 90,
    },
    {
      id: "suggested",
      header: "Suggested Match",
      accessorFn: (candidate: CandidateSelectionRow): string =>
        candidate.isProposedMatch ? "Yes" : "",
      size: 130,
    },
    {
      id: "contract_number",
      header: "Agtech Contract No",
      accessorFn: (candidate: CandidateSelectionRow): number => candidate.contractNumber,
    },
    {
      id: "customer",
      header: "Customer",
      accessorFn: (candidate: CandidateSelectionRow): string => candidate.customer,
    },
    {
      id: "contract_type",
      header: "Contract Type",
      accessorFn: (candidate: CandidateSelectionRow): string => candidate.contractType,
    },
    {
      id: "commodity",
      header: "Commodity",
      accessorFn: (candidate: CandidateSelectionRow): string => commodityDisplayName(candidate.commodity),
    },
    {
      id: "quantity",
      header: "Quantity",
      accessorFn: (candidate: CandidateSelectionRow): string => candidate.quantity ?? "",
      cell: ({ row }): string => formatNumberToDisplay(row.original.quantity),
    },
    {
      id: "contract_date",
      header: "Contract Date",
      accessorFn: (candidate: CandidateSelectionRow): string => candidate.contractDate ?? "",
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (value: unknown): string =>
        typeof value === "string"
          ? parseContractCalendarDate(value)?.format("YYYY/MM/DD") ?? ""
          : "",
      filterParseDateFn: (value: unknown): moment.Moment | null =>
        typeof value === "string" ? parseContractCalendarDate(value) : null,
      cell: ({ row }): string => formatContractCalendarDate(row.original.contractDate),
    },
    {
      id: "location",
      header: "Location",
      accessorFn: (candidate: CandidateSelectionRow): string => candidate.location ?? "",
    },
    {
      id: "client_id",
      header: "Client ID",
      accessorFn: (candidate: CandidateSelectionRow): string => candidate.clientId,
    },
  ];

  return (
    <Dialog open={proposal !== null} onOpenChange={(open: boolean): void => { if (!open) onClose(); }}>
      <DialogContent className="max-w-none w-[90vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select an Agtech contract</DialogTitle>
          <DialogDescription>
            Selecting a row only changes the proposed pairing. No contract is updated until Apply Selected is confirmed.
          </DialogDescription>
        </DialogHeader>
        {proposal ? (
          <div className="rounded-md border bg-muted/30 p-4">
            <p className="mb-3 text-sm font-semibold">Selected Lighthouse contract</p>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3 lg:grid-cols-6">
              <div>
                <dt className="text-muted-foreground">Contract Type</dt>
                <dd className="font-medium">{proposal.target.contract_type_display || "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Commodity</dt>
                <dd className="font-medium">
                  {commodityDisplayName(proposal.target.primary.commodity) || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Quantity</dt>
                <dd className="font-medium">
                  {formatNumberToDisplay(proposal.target.primary.quantity) || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Customer</dt>
                <dd className="font-medium">{proposal.target.Customer?.long_name ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Contract Date</dt>
                <dd className="font-medium">
                  {formatContractCalendarDate(proposal.target.primary.contract_date) || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">P/S</dt>
                <dd className="font-medium">
                  {proposal.target.fallback.purchase_sale_type ?? "—"}
                </dd>
              </div>
            </dl>
          </div>
        ) : null}
        {failedPage ? <p className="text-sm text-destructive">{failedPage.message}</p> : null}
        {listResult.isFetching ? (
          <p className="text-sm text-muted-foreground">
            Loading Agtech contracts... {listedCandidates.length} loaded
          </p>
        ) : null}
        <DataTable
          data={candidates}
          columns={columns}
          enableSorting
          enableMultiSort
          enableFiltering
          enableColumnResizing
          enableColumnVisibility
          enableVirtualization
          virtualizationRowHeight={52}
          virtualizationOverscan={2}
          filterStyle="select"
          filterMode="internal"
          rowClickable
          onRowClick={(candidate: CandidateSelectionRow): void => onSelect(candidate)}
          emptyMessage={listResult.isLoading ? "Loading Agtech contracts..." : "No unmatched Agtech contracts found"}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={selectedChangeId === undefined}
            onClick={onClear}
          >
            Clear Selection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PairingConfirmationDialog({
  items,
  isSubmitting,
  onClose,
  onConfirm,
}: {
  items: PairContractChangeItem[];
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={items.length > 0} onOpenChange={(open: boolean): void => { if (!open && !isSubmitting) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Apply contract pairings?</DialogTitle>
          <DialogDescription>
            This will assign Agtech contract numbers to {items.length} selected Lighthouse contract{items.length === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={isSubmitting} onClick={onClose}>Cancel</Button>
          <Button type="button" disabled={isSubmitting} onClick={onConfirm}>
            {isSubmitting ? "Applying..." : "Apply Pairings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkPairResultDialog({
  result,
  onClose,
}: {
  result: BulkPairContractChangesResult | null;
  onClose: () => void;
}) {
  const failures = result?.items.filter(
    (item: BulkPairContractChangesResult["items"][number]): boolean => item.outcome === "failed",
  ) ?? [];

  return (
    <Dialog open={result !== null} onOpenChange={(open: boolean): void => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Contract pairing results</DialogTitle>
          <DialogDescription>
            Paired {result?.paired_count ?? 0}; failed {result?.failed_count ?? 0}.
          </DialogDescription>
        </DialogHeader>
        {failures.length > 0 ? (
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {failures.map((failure: BulkPairContractChangesResult["items"][number]): JSX.Element => (
              <div key={failure.change_id} className="rounded border border-destructive/40 p-3 text-sm">
                {failure.outcome === "failed" ? (
                  <>
                    <p className="font-medium">A selected contract could not be paired</p>
                    <p>{failure.message}</p>
                  </>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractTargetMatchingDashboard({
  targetType,
}: {
  targetType: ContractMatchTargetType;
}) {
  const [state, dispatch] = useReducer(matchingReducer, initialState);
  const { toast } = useToast();
  const proposalsResult = useContractMatchProposals({
    page: state.page,
    page_size: PAGE_SIZE,
    target_type: targetType,
  });
  const pairContractChange = usePairContractChange();
  const bulkPairContractChanges = useBulkPairContractChanges();
  const proposalData = proposalsResult.data?.success ? proposalsResult.data.data : null;
  const proposals = proposalData?.items ?? noProposals;
  const activeProposal = proposals.find(
    (proposal: ContractMatchProposalResult): boolean => proposal.target.target_id === state.activeTargetId,
  ) ?? null;
  const tableRows = useMemo(
    (): MatchingTableRow[] => proposals.map((proposal: ContractMatchProposalResult): MatchingTableRow => ({
      proposal,
      selected: selectedCandidate(proposal, state.selectedCandidates),
      matchStatus: isSelectionCleared(proposal, state.selectedCandidates)
        ? "Cleared"
        : isManuallyMatched(proposal, state.selectedCandidates)
        ? "Manually matched"
        : proposalStatus(proposal),
      reason: hasSelectionOverride(proposal, state.selectedCandidates)
        ? ""
        : reasonLabel(proposal.reason),
    })),
    [proposals, state.selectedCandidates],
  );

  const requestPairing = (rows: MatchingTableRow[]): void => {
    const items = rows.flatMap((row: MatchingTableRow): PairContractChangeItem[] => {
      return row.selected ? [{
        change_id: row.selected.changeId,
        target_type: targetType,
        target_id: row.proposal.target.target_id,
      }] : [];
    });
    if (items.length > 0) dispatch({ type: "request_pairing", items });
  };

  const submitPairing = async (): Promise<void> => {
    if (state.isSubmitting || state.pendingItems.length === 0) return;
    dispatch({ type: "start_submit" });

    try {
      if (state.pendingItems.length === 1) {
        const parsed = PairContractChangeItemValidation.safeParse(state.pendingItems[0]);
        if (!parsed.success) {
          toast({
            title: "Unable to apply pairing",
            description: "The selected pairing is incomplete. Select the contract again and retry.",
            variant: "destructive",
          });
          dispatch({ type: "submit_failed" });
          return;
        }
        const result = await pairContractChange(parsed.data);
        if (!result.success) {
          toast({
            title: "Contract was not paired",
            description: result.message,
            variant: "destructive",
          });
          dispatch({ type: "submit_failed" });
          return;
        }
        toast({
          title: "Contract paired",
          description: `Agtech contract number ${result.data.assigned_contract_no} was assigned successfully.`,
        });
        dispatch({ type: "single_completed" });
        return;
      }

      const parsed = BulkPairContractChangesRequestValidation.safeParse({ items: state.pendingItems });
      if (!parsed.success) {
        toast({
          title: "Unable to apply selected pairings",
          description: "One or more selected pairings are incomplete. Select the contracts again and retry.",
          variant: "destructive",
        });
        dispatch({ type: "submit_failed" });
        return;
      }
      const result = await bulkPairContractChanges(parsed.data);
      if (!result.success) {
        toast({
          title: "Selected contracts were not paired",
          description: result.message,
          variant: "destructive",
        });
        dispatch({ type: "submit_failed" });
        return;
      }
      dispatch({ type: "bulk_completed", result: result.data });
    } catch {
      toast({
        title: "Unable to reach the server",
        description: "Check your connection and try applying the selected pairings again.",
        variant: "destructive",
      });
      dispatch({ type: "submit_failed" });
    }
  };

  const columns: DataTableColumn<MatchingTableRow>[] = [
    {
      id: "selection-box",
      enableFiltering: false,
      enableSorting: false,
      enableGrouping: false,
      disableRowClick: true,
      header: ({ table }: { table: Table<MatchingTableRow> }): JSX.Element => {
        const eligibleRows = table.getFilteredRowModel().rows.filter(
          (row: Row<MatchingTableRow>): boolean => row.original.selected !== undefined,
        );
        const allSelected = eligibleRows.length > 0 && eligibleRows.every(
          (row: Row<MatchingTableRow>): boolean => row.getIsSelected(),
        );
        const someSelected = eligibleRows.some(
          (row: Row<MatchingTableRow>): boolean => row.getIsSelected(),
        );
        return (
          <IndeterminateCheckbox
            className="mt-2 ml-1"
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            disabled={eligibleRows.length === 0}
            onChange={(event): void => {
              eligibleRows.forEach((row: Row<MatchingTableRow>): void => {
                row.toggleSelected(event.currentTarget.checked);
              });
            }}
          />
        );
      },
      cell: ({ row }): JSX.Element => {
        const canSelect = row.original.selected !== undefined;
        return (
          <IndeterminateCheckbox
            className="ml-1"
            checked={row.getIsSelected()}
            disabled={!canSelect}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        );
      },
      size: 50,
    },
    {
      id: "contract_type",
      header: "Contract Type",
      accessorFn: (row: MatchingTableRow): string =>
        row.proposal.target.contract_type_display,
    },
    {
      id: "commodity",
      header: "Commodity",
      accessorFn: (row: MatchingTableRow): string =>
        commodityDisplayName(row.proposal.target.primary.commodity),
    },
    {
      id: "quantity",
      header: "Quantity",
      accessorFn: (row: MatchingTableRow): string => row.proposal.target.primary.quantity ?? "",
      cell: ({ row }): string => formatNumberToDisplay(row.original.proposal.target.primary.quantity),
    },
    {
      id: "customer",
      header: "Customer",
      accessorFn: (row: MatchingTableRow): string => row.proposal.target.Customer?.long_name ?? "",
    },
    {
      id: "contract_date",
      header: "Contract Date",
      accessorFn: (row: MatchingTableRow): string => row.proposal.target.primary.contract_date ?? "",
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (value: unknown): string =>
        value ? moment.tz(value as string, CommonDestinationTimezone).format("YYYY/MM/DD") : "",
      filterParseDateFn: (value: unknown) =>
        value ? moment.tz(value as string, CommonDestinationTimezone) : null,
      cell: ({ row }): string => row.original.proposal.target.primary.contract_date
        ? moment.tz(row.original.proposal.target.primary.contract_date, CommonDestinationTimezone).format("MM/DD/YYYY")
        : "",
    },
    {
      id: "purchase_sale_type",
      header: "P/S",
      accessorFn: (row: MatchingTableRow): string => row.proposal.target.fallback.purchase_sale_type ?? "",
    },
    {
      id: "suggested_contract_number",
      header: "Suggested Contract No",
      accessorFn: (row: MatchingTableRow): string => row.selected?.contractNumber.toString() ?? "",
    },
    {
      id: "match_status",
      header: "Match Status",
      accessorFn: (row: MatchingTableRow): string => row.matchStatus,
    },
    {
      id: "reason",
      header: "Reason",
      accessorFn: (row: MatchingTableRow): string => row.reason,
    },
  ];

  const bulkButtons = [({ table }: { table: Table<MatchingTableRow> }): JSX.Element => {
    const selectedRows = table.getFilteredSelectedRowModel().rows.map(
      (row: Row<MatchingTableRow>): MatchingTableRow => row.original,
    );
    return (
      <Button
        type="button"
        disabled={selectedRows.length === 0 || state.isSubmitting}
        onClick={(): void => requestPairing(selectedRows)}
      >
        Apply Selected ({selectedRows.length})
      </Button>
    );
  }];

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      <div className="mb-3 flex items-center gap-3">
        <p className="text-sm text-muted-foreground">
          Review suggested Agtech contract numbers. Opening a row lets you choose among the eligible candidates returned by matching.
        </p>
        {proposalsResult.isFetching ? <span className="text-sm text-muted-foreground">Loading...</span> : null}
      </div>
      {proposalsResult.data && !proposalsResult.data.success ? (
        <p className="mb-3 text-sm text-destructive">{proposalsResult.data.message}</p>
      ) : null}
      {(proposalData?.stale_candidates.length ?? 0) > 0 ? (
        <p className="mb-3 text-sm text-amber-700">
          {proposalData?.stale_candidates.length} stale Agtech candidate{proposalData?.stale_candidates.length === 1 ? " was" : "s were"} excluded.
        </p>
      ) : null}
      <DataTable
        key={`contract-matching-${state.page}-${state.selectionVersion}`}
        stickyHeaders
        tableClassName="max-h-[calc(100vh-240px)] overflow-y-scroll"
        data={tableRows}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnVisibility
        enableColumnResizing
        enableColumnPinning
        enableMultiFilter
        enableMultiSelect
        defaultCondensed
        enableVirtualization
        virtualizationRowHeight={49}
        virtualizationOverscan={2}
        filterStyle="select"
        filterMode="internal"
        customToolbarButtons={bulkButtons}
        rowClickable
        onRowClick={(row: MatchingTableRow): void =>
          dispatch({ type: "open_candidates", targetId: row.proposal.target.target_id })
        }
        emptyMessage={proposalsResult.isLoading ? "Loading contract match proposals..." : "No unnumbered contracts found"}
      />
      <div className="flex items-center justify-between py-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={state.page === 1 || proposalsResult.isFetching}
          onClick={(): void => dispatch({ type: "set_page", page: state.page - 1 })}
        >
          <ChevronLeft className="mr-1 h-4 w-4" /> Previous
        </Button>
        <span className="text-sm text-muted-foreground">Page {state.page}</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!proposalData?.has_next_page || proposalsResult.isFetching}
          onClick={(): void => dispatch({ type: "set_page", page: state.page + 1 })}
        >
          Next <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
      <CandidateSelectionDialog
        proposal={activeProposal}
        proposals={proposals}
        selectedChangeId={activeProposal
          ? selectedCandidate(activeProposal, state.selectedCandidates)?.changeId
          : undefined}
        selectedCandidates={state.selectedCandidates}
        onClear={(): void => {
          if (!activeProposal) return;
          dispatch({
            type: "clear_candidate",
            targetId: activeProposal.target.target_id,
          });
        }}
        onClose={(): void => dispatch({ type: "close_candidates" })}
        onSelect={(candidate: CandidateSelectionRow): void => {
          if (!activeProposal) return;
          dispatch({
            type: "select_candidate",
            targetId: activeProposal.target.target_id,
            candidate: {
              changeId: candidate.changeId,
              contractNumber: candidate.contractNumber,
            },
          });
        }}
      />
      <PairingConfirmationDialog
        items={state.pendingItems}
        isSubmitting={state.isSubmitting}
        onClose={(): void => dispatch({ type: "cancel_pairing" })}
        onConfirm={submitPairing}
      />
      <BulkPairResultDialog
        result={state.bulkResult}
        onClose={(): void => dispatch({ type: "close_bulk_result" })}
      />
    </section>
  );
}

export function ContractsMatchingDashboard() {
  const [targetType, setTargetType] = useState<ContractMatchTargetType>("general_contract");

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="flex gap-2 px-6 pt-4 lg:px-8">
        <Button
          type="button"
          variant={targetType === "general_contract" ? "default" : "outline"}
          onClick={(): void => setTargetType("general_contract")}
        >
          General
        </Button>
        <Button
          type="button"
          variant={targetType === "trading_contract" ? "default" : "outline"}
          onClick={(): void => setTargetType("trading_contract")}
        >
          OTC
        </Button>
      </div>
      <ContractTargetMatchingDashboard key={targetType} targetType={targetType} />
    </div>
  );
}
